/** 走查与失效判据（validation 屏）的契约解析。
 *
 * 形状以后端为准（主人 2026-10-08 裁决）：列表是 `GET /validation` 的 `{<sid>: status}`，
 * 明细是 `GET /validation/{sid}` 的 `{status, latest_weekly, latest_review, latest_judgement, needs_disposition}`。
 * 是否需处置、判定是否通过都由后端给出，这里只校验形状、不重算指标、不重判阈值。
 * 指标块按权益口径（裁决 B7）：`net_pnl` 是主口径净收益，`net_pnl_cash` 只是现金变动对照项；
 * 改口径前落盘的旧产物没有 `basis` / `net_pnl`，解析为 `null`，屏上标「旧产物（现金口径）」。
 */

import { apiUrl, expectRecord, optionalNumber, optionalString, query, requireBoolean, requireNumber, requireString } from "../api/client";
import { ContractError } from "../api/errors";

export interface MatchedRule {
  metric: string;
  value: number | null;
  operator: string;
  threshold: number | null;
  action: string;
  factor: number | null;
  window_days: number | null;
  source: string | null;
}

export interface HistoryRow {
  action: string;
  at_utc: number | null;
  reason: string;
  operator: string | null;
}

export interface ValidationStatus {
  evaluated: boolean;
  enabled: boolean;
  weight_factor: number | null;
  reason: string | null;
  note: string | null;
  since_utc: number | null;
  needs_disposition: boolean;
  needs_disposition_reason: string | null;
  last_review_utc: number | null;
  last_evaluated_utc: number | null;
  matched: MatchedRule[];
  history: HistoryRow[];
}

export interface ValidationRow {
  strategy_id: string;
  status: ValidationStatus;
}

export interface MetricBlock {
  basis: string | null;
  net_pnl: number | null;
  net_pnl_cash: number | null;
  cumulative_return: number;
  annualized_return: number;
  max_drawdown: number;
  sharpe: number;
  trading_days: number;
  start_date: string;
  end_date: string;
}

export interface WeeklyReport {
  as_of: string;
  /** `null` = 研究侧净值未提供（后端写 `{status: "未提供"}`）。 */
  in_sample: MetricBlock | null;
  out_of_sample: MetricBlock;
  rolling: { window_start: string; window_end: string; rolling_return: number }[];
  summary_text: string;
}

export interface MonthlyReview {
  as_of: string;
  conclusion: string;
  summary_text: string;
}

export interface Criterion {
  criterion: string;
  threshold: unknown;
  value: unknown;
  pass: boolean;
}

export interface Judgement {
  as_of: string;
  observation_complete: boolean;
  observation_months: number;
  observation_start: string;
  observation_end: string;
  parameter_reset_date: string | null;
  criteria: Criterion[];
  pass: boolean;
  summary_text: string;
}

export interface ValidationDetail {
  status: ValidationStatus;
  latest_weekly: WeeklyReport | null;
  latest_review: MonthlyReview | null;
  latest_judgement: Judgement | null;
  needs_disposition: boolean;
}

const optionalBool = (o: Record<string, unknown>, k: string): boolean => o[k] === true;

function parseStatus(raw: unknown, url: string): ValidationStatus {
  const s = expectRecord(raw, url, "验证状态");
  const evaluated = s["evaluated"] === undefined ? true : requireBoolean(s, "evaluated", url);
  const evidence = s["evidence"] && typeof s["evidence"] === "object" ? (s["evidence"] as Record<string, unknown>) : {};
  const matched = Array.isArray(evidence["matched"]) ? evidence["matched"] : [];
  const history = Array.isArray(s["history"]) ? s["history"] : [];
  return {
    evaluated,
    enabled: requireBoolean(s, "enabled", url),
    weight_factor: evaluated ? requireNumber(s, "weight_factor", url) : optionalNumber(s, "weight_factor"),
    reason: optionalString(s, "reason"),
    note: optionalString(s, "note"),
    since_utc: optionalNumber(s, "since_utc"),
    needs_disposition: optionalBool(s, "needs_disposition"),
    needs_disposition_reason: optionalString(s, "needs_disposition_reason"),
    last_review_utc: optionalNumber(s, "last_review_utc"),
    last_evaluated_utc: optionalNumber(s, "last_evaluated_utc"),
    matched: matched.map((m, i) => {
      const r = expectRecord(m, url, `evidence.matched[${i}]`);
      return {
        metric: requireString(r, "metric", url),
        value: optionalNumber(r, "value"),
        operator: requireString(r, "operator", url),
        threshold: optionalNumber(r, "threshold"),
        action: requireString(r, "action", url),
        factor: optionalNumber(r, "factor"),
        window_days: optionalNumber(r, "window_days"),
        source: optionalString(r, "source"),
      };
    }),
    history: history.map((h, i) => {
      const r = expectRecord(h, url, `history[${i}]`);
      return {
        action: requireString(r, "action", url),
        at_utc: optionalNumber(r, "at_utc"),
        reason: optionalString(r, "reason") ?? "",
        operator: optionalString(r, "operator"),
      };
    }),
  };
}

export function parseValidationList(body: unknown): ValidationRow[] {
  const url = apiUrl("/validation");
  const record = expectRecord(body, url, "验证状态表");
  return Object.entries(record).map(([strategy_id, status]) => ({ strategy_id, status: parseStatus(status, url) }));
}

function parseMetrics(raw: unknown, url: string, what: string): MetricBlock {
  const m = expectRecord(raw, url, what);
  return {
    basis: optionalString(m, "basis"),
    net_pnl: optionalNumber(m, "net_pnl"),
    net_pnl_cash: optionalNumber(m, "net_pnl_cash"),
    cumulative_return: requireNumber(m, "cumulative_return", url),
    annualized_return: requireNumber(m, "annualized_return", url),
    max_drawdown: requireNumber(m, "max_drawdown", url),
    sharpe: requireNumber(m, "sharpe", url),
    trading_days: requireNumber(m, "trading_days", url),
    start_date: requireString(m, "start_date", url),
    end_date: requireString(m, "end_date", url),
  };
}

function parseWeekly(raw: unknown, url: string): WeeklyReport | null {
  if (raw === null || raw === undefined) return null;
  const w = expectRecord(raw, url, "latest_weekly");
  const inSample = expectRecord(w["in_sample"], url, "latest_weekly.in_sample");
  const rolling = Array.isArray(w["rolling"]) ? w["rolling"] : [];
  return {
    as_of: requireString(w, "as_of", url),
    in_sample: inSample["status"] === "未提供" ? null : parseMetrics(inSample, url, "in_sample"),
    out_of_sample: parseMetrics(w["out_of_sample"], url, "out_of_sample"),
    rolling: rolling.map((item, i) => {
      const r = expectRecord(item, url, `rolling[${i}]`);
      return {
        window_start: requireString(r, "window_start", url),
        window_end: requireString(r, "window_end", url),
        rolling_return: requireNumber(r, "rolling_return", url),
      };
    }),
    summary_text: optionalString(w, "summary_text") ?? "",
  };
}

function parseReview(raw: unknown, url: string): MonthlyReview | null {
  if (raw === null || raw === undefined) return null;
  const r = expectRecord(raw, url, "latest_review");
  return {
    as_of: requireString(r, "as_of", url),
    conclusion: requireString(r, "conclusion", url),
    summary_text: optionalString(r, "summary_text") ?? "",
  };
}

function parseJudgement(raw: unknown, url: string): Judgement | null {
  if (raw === null || raw === undefined) return null;
  const j = expectRecord(raw, url, "latest_judgement");
  const criteria = j["criteria"];
  if (!Array.isArray(criteria)) throw new ContractError("latest_judgement 缺少数组字段 `criteria`", url, j);
  return {
    as_of: requireString(j, "as_of", url),
    observation_complete: requireBoolean(j, "observation_complete", url),
    observation_months: requireNumber(j, "observation_months", url),
    observation_start: requireString(j, "observation_start", url),
    observation_end: requireString(j, "observation_end", url),
    parameter_reset_date: optionalString(j, "parameter_reset_date"),
    criteria: criteria.map((c, i) => {
      const r = expectRecord(c, url, `criteria[${i}]`);
      return { criterion: requireString(r, "criterion", url), threshold: r["threshold"], value: r["value"], pass: requireBoolean(r, "pass", url) };
    }),
    pass: requireBoolean(j, "pass", url),
    summary_text: optionalString(j, "summary_text") ?? "",
  };
}

export function parseValidationDetail(body: unknown, sid: string): ValidationDetail {
  const url = apiUrl(`/validation/${encodeURIComponent(sid)}`);
  const d = expectRecord(body, url, "验证明细");
  return {
    status: parseStatus(d["status"], url),
    latest_weekly: parseWeekly(d["latest_weekly"], url),
    latest_review: parseReview(d["latest_review"], url),
    latest_judgement: parseJudgement(d["latest_judgement"], url),
    needs_disposition: requireBoolean(d, "needs_disposition", url),
  };
}

export const readValidationList = () => query<unknown>("/validation").then(parseValidationList);
export const readValidationDetail = (sid: string) =>
  query<unknown>(`/validation/${encodeURIComponent(sid)}`).then((body) => parseValidationDetail(body, sid));

/** 处置状态的一句话结论与色——未评估不许显示成「正常」。 */
export function dispositionOf(status: ValidationStatus): { label: string; tone: "ok" | "warn" | "bad" | "neutral" } {
  if (!status.evaluated) return { label: "未评估（还没跑过走查）", tone: "neutral" };
  if (!status.enabled) return { label: "停用", tone: "bad" };
  if (status.needs_disposition) return { label: "需处置", tone: "warn" };
  if (status.weight_factor !== null && status.weight_factor < 1) return { label: "降权", tone: "warn" };
  return { label: "正常", tone: "ok" };
}

/** 受限或需处置的策略才可恢复（与后端 409 判据一致，最终以服务端为准）。 */
export function restorable(status: ValidationStatus): boolean {
  return status.evaluated && (!status.enabled || status.needs_disposition || (status.weight_factor ?? 1) < 1);
}

/** 指标块的口径标注。 */
export function basisLabel(m: MetricBlock): string {
  if (m.basis === "equity") return "权益口径";
  if (m.basis === "cash") return "现金口径（行里无权益）";
  if (m.basis === "mixed") return "混合（部分行无权益）";
  return "旧产物（改口径前生成，按现金口径）";
}
